import React, { useState } from 'react';
import WorkflowBar, { WorkflowStep } from './WorkflowBar';
import PDFViewer from './PDFViewer';
import FormControl from '../common/FormControl';
import './WorkflowDemo.css';

type DemoMode = 'workflow' | 'pdf';

const SECTIONS: { key: DemoMode; label: string }[] = [
  { key: 'workflow', label: 'Workflow Bar' },
  { key: 'pdf', label: 'PDF Viewer' },
];

const WORKFLOW_FEATURES = [
  'Dynamic vertical line heights based on description length',
  'Three status states: Completed, In-Progress, Upcoming',
  'Custom icons or default status indicators',
  'Responsive design for mobile devices',
  'Visual progression with color coding',
  'Animated pulse effect for in-progress steps',
];

const PDF_FEATURES = [
  'Left-right layout with independent scrolling sections',
  'Category-based document organization with search functionality',
  'Markdown rendering with styled formatting',
  'Multi-format support: PDF, HTML, and Markdown files',
  'JSON-driven document metadata and relative paths',
];

const WorkflowDemo: React.FC = () => {
  const [demoMode, setDemoMode] = useState<DemoMode>('workflow');
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('flight-booking');

  const workflowData: Record<string, WorkflowStep[]> = {
    'flight-booking': [
      { id: 'login', title: 'Login', description: 'Enter your credential data', status: 'completed', icon: '👤' },
      { id: 'booking', title: 'Booking flight ticket', description: 'Enter ticket details', status: 'completed', icon: '✈️' },
      { id: 'booked', title: 'Flight Ticket Booked', description: 'Booked details', status: 'in-progress', icon: '📄' },
      { id: 'confirmed', title: 'Confirmed', description: '', status: 'upcoming', icon: '✅' },
    ],
    'project-deployment': [
      { id: 'planning', title: 'Project Planning', description: 'Define project scope, requirements, and create detailed project plan with timeline and resource allocation.', status: 'completed' },
      { id: 'development', title: 'Development Phase', description: 'Code implementation, unit testing, and initial integration.', status: 'completed' },
      { id: 'testing', title: 'Quality Assurance Testing', description: 'Comprehensive testing including functional, integration, performance, and security testing.', status: 'in-progress' },
      { id: 'staging', title: 'Staging Deployment', description: 'Deploy to staging environment for final validation and user acceptance testing.', status: 'upcoming' },
      { id: 'production', title: 'Production Release', description: 'Go-live deployment to production environment with monitoring and rollback plan.', status: 'upcoming' },
    ],
    'compliance-audit': [
      { id: 'initiation', title: 'Audit Initiation', description: 'Audit planning and scoping phase with stakeholder notifications.', status: 'completed' },
      { id: 'fieldwork', title: 'Fieldwork Execution', description: 'Conducting interviews, testing controls, and collecting evidence.', status: 'completed' },
      { id: 'findings', title: 'Findings Analysis', description: 'Analyzing collected evidence, documenting findings, and developing recommendations.', status: 'in-progress' },
      { id: 'reporting', title: 'Final Reporting', description: 'Preparation and delivery of final audit report.', status: 'upcoming' },
    ],
  };

  const workflowOptions = [
    { key: 'flight-booking', label: 'Flight Booking Process', description: 'User journey for flight ticket booking' },
    { key: 'project-deployment', label: 'Project Deployment', description: 'Software development lifecycle' },
    { key: 'compliance-audit', label: 'Compliance Audit', description: 'Audit execution workflow' },
  ];

  const activeFeatures = demoMode === 'workflow' ? WORKFLOW_FEATURES : PDF_FEATURES;

  return (
    <div className="flex h-full overflow-hidden">

      {/* ── Left nav card ──────────────────────────────────────────── */}
      <div className="netbody-card side-nav-card">
        <div className="side-nav-card-heading">Demo Mode</div>
        <nav aria-label="Demo mode navigation">
          {SECTIONS.map(section => (
            <button
              key={section.key}
              onClick={() => setDemoMode(section.key)}
              className={`side-panel-menu-item${demoMode === section.key ? ' side-panel-menu-item--active' : ''}`}
              data-testid={`NAV_workflow_demo_mode_${section.key}`}
            >
              {section.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Right card: toolbar + content ──────────────────────────── */}
      <div className="netbody-card side-content-card flex flex-col flex-1 min-w-0">

        {demoMode === 'workflow' && (
          <div className="netbody-header">
            <div className="flex items-center gap-2 ml-auto">
              <FormControl.SearchSelect
                value={selectedWorkflow}
                onChange={v => setSelectedWorkflow(String(v ?? 'flight-booking'))}
                className="filter-status"
                data-testid="SEL_workflow_demo_workflow"
                options={workflowOptions.map(opt => ({ value: opt.key, label: opt.label }))}
                noSearchIcon
              />
              <span className="text-sm text-gray-500">
                {workflowOptions.find(o => o.key === selectedWorkflow)?.description}
              </span>
            </div>
          </div>
        )}

        <div className="netbody-content">
          {demoMode === 'workflow' ? (
            <div className="scroll-container">
              <div className="demo-content">
                <WorkflowBar steps={workflowData[selectedWorkflow]} />
              </div>
              <div className="demo-features" style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  Features Demonstrated
                </h3>
                <ul style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.8', paddingLeft: '16px' }}>
                  {activeFeatures.map(f => <li key={f}>{f}</li>)}
                </ul>
              </div>
            </div>
          ) : (
            <div className="scroll-container-nofilters">
              <div className="demo-content">
                <PDFViewer />
              </div>
              <div className="demo-features" style={{ marginTop: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                  Features Demonstrated
                </h3>
                <ul style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.8', paddingLeft: '16px' }}>
                  {activeFeatures.map(f => <li key={f}>{f}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default WorkflowDemo;
